<template>
  <v-container fluid>
    <v-card>
      <v-card-title>Контроль Суфлёра</v-card-title>

      <v-card-text>
        <v-form ref="form" lazy-validation>
          <v-row>
            <v-col cols="12" sm="6" md="4" lg="3">
              <TimeInterval v-model="filters.period" />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="3">
              <ProjectSelector v-model="filters.project" single />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="3">
              <v-select
                v-model="filters.operator_rating"
                :items="ratingOptions"
                item-text="text"
                item-value="value"
                label="Оценка оператора"
                hide-details
              />
            </v-col>

            <v-col cols="12" sm="6" md="4" lg="3">
              <v-select
                v-model="filters.sort"
                :items="sortOptions"
                item-text="text"
                item-value="value"
                label="Сортировка"
                hide-details
              />
            </v-col>

            <v-col cols="12" class="d-flex justify-end align-end">
              <v-btn
                color="success"
                :disabled="loading || !canSearch"
                :loading="loading"
                @click="loadReport(true)"
              >
                Показать
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card v-if="summary" class="mt-5">
      <v-card-text>
        <v-row class="summary-row">
          <v-col cols="12" md="4" class="summary-tile">
            <div class="summary-label">Проверено фраз РГ</div>
            <div class="summary-value">
              <span class="summary-number success--text">{{ formatPercent(summary.correct_percent) }}</span>
              <span class="summary-hint">
                корректность · {{ summary.correct }} из {{ summary.checked }}
              </span>
            </div>
          </v-col>

          <v-col cols="12" md="4" class="summary-tile">
            <div class="summary-label">Оценки операторов</div>
            <div class="summary-value">
              <span class="summary-number success--text">{{ formatPercent(summary.likes_percent) }}</span>
              <span class="summary-hint">
                лайков · {{ summary.likes }} из {{ summary.rated }}
                ({{ summary.likes }} <v-icon x-small color="success">mdi-thumb-up</v-icon>
                / {{ summary.dislikes }} <v-icon x-small color="error">mdi-thumb-down</v-icon>)
              </span>
            </div>
          </v-col>

          <v-col cols="12" md="4" class="summary-tile">
            <div class="summary-label">Выдано подсказок</div>
            <div class="summary-value">
              <span class="summary-number">{{ summary.hints }}</span>
              <span class="summary-hint">
                проверено {{ formatPercent(summary.hints_checked_percent) }}
              </span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="reportLoaded" class="mt-5">
      <v-card-text>
        <div v-if="paginate.last_page > 1" class="d-flex justify-center mb-4">
          <v-pagination
            v-model="paginate.current_page"
            :length="paginate.last_page"
            :total-visible="10"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
            @input="loadReport(false)"
          />
        </div>

        <div class="table-wrap">
          <v-data-table
            :headers="callHeaders"
            :items="calls"
            :loading="loading"
            :items-per-page="calls.length || paginate.per_page"
            :expanded.sync="expanded"
            show-expand
            item-key="_rowKey"
            loading-text="Загрузка…"
            no-data-text="Ничего не найдено"
            hide-default-footer
            dense
            class="elevation-0 call-table"
            @click:row="onCallRowClick"
          >
            <template #item.date="{ item }">
              {{ item.date || '—' }}
            </template>

            <template #item.project="{ item }">
              {{ item.project || '—' }}
            </template>

            <template #item.count_phrases="{ item }">
              {{ item.count_phrases != null ? item.count_phrases : '—' }}
            </template>

            <template #item.count_phrases_with_results="{ item }">
              {{ item.count_phrases_with_results != null ? item.count_phrases_with_results : '—' }}
            </template>

            <template #item.correct_ratio="{ item }">
              <span
                v-if="item.count_checked"
                class="ratio-cell"
                :class="{ 'error--text font-weight-bold': isLowRatio(item.count_correct, item.count_checked) }"
              >
                {{ item.count_correct }} / {{ item.count_checked }}
              </span>
              <span v-else>—</span>
            </template>

            <template #item.likes_ratio="{ item }">
              <span
                v-if="item.count_rated"
                class="ratio-cell"
                :class="{ 'error--text font-weight-bold': isLowRatio(item.count_likes, item.count_rated) }"
              >
                {{ item.count_likes }} / {{ item.count_rated }}
              </span>
              <span v-else>—</span>
            </template>

            <template #item.is_checked="{ item }">
              <v-icon v-if="item.is_checked === true" color="success">mdi-check</v-icon>
            </template>

            <template #item.call_link="{ item }">
              <v-btn
                v-if="item.call_link"
                small
                color="primary"
                :href="item.call_link"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                <v-icon left small>mdi-open-in-new</v-icon>
                Открыть
              </v-btn>
              <span v-else>—</span>
            </template>

            <template #expanded-item="{ headers, item }">
              <td :colspan="headers.length" class="pa-4">
                <v-data-table
                  :headers="phraseHeaders"
                  :items="item.items"
                  :items-per-page="item.items.length || 1"
                  item-key="id"
                  no-data-text="Нет фраз"
                  hide-default-footer
                  dense
                  class="elevation-0"
                >
                  <template #item.date="{ item }">
                    {{ item.date || '—' }}
                  </template>

                  <template #item.customer_phrase="{ item }">
                    {{ item.customer_phrase || '—' }}
                  </template>

                  <template #item.topic="{ item }">
                    {{ item.topic || '—' }}
                  </template>

                  <template #item.topic_description="{ item }">
                    <span class="description-cell" :title="item.topic_description || ''">
                      {{ formatDescription(item.topic_description) }}
                    </span>
                  </template>

                  <template #item.operator_phrase="{ item }">
                    {{ item.operator_phrase || '—' }}
                  </template>

                  <template #item.operator_rating="{ item }">
                    <span v-if="item.operator_rating" class="rating-cell">
                      <v-icon
                        small
                        :color="item.operator_rating === 'like' ? 'success' : 'error'"
                      >
                        {{ item.operator_rating === 'like' ? 'mdi-thumb-up' : 'mdi-thumb-down' }}
                      </v-icon>
                      {{ item.operator_rating_title }}
                    </span>
                    <span v-else>—</span>
                  </template>

                  <template #item.is_correct="{ item }">
                    <div v-if="item.is_correct === null" class="correctness-actions">
                      <v-btn
                        x-small
                        color="success"
                        :loading="!!correctnessSaving[item.id]"
                        :disabled="!!correctnessSaving[item.id]"
                        @click="onIsCorrectChange(item, true)"
                      >
                        Корректно
                      </v-btn>
                      <v-btn
                        x-small
                        color="error"
                        :loading="!!correctnessSaving[item.id]"
                        :disabled="!!correctnessSaving[item.id]"
                        @click="onIsCorrectChange(item, false)"
                      >
                        Не корректно
                      </v-btn>
                    </div>
                    <div v-else class="correctness-value">
                      <span :class="item.is_correct ? 'success--text' : 'error--text'">
                        {{ item.is_correct ? 'Корректно' : 'Не корректно' }}
                      </span>
                      <v-btn
                        icon
                        x-small
                        :loading="!!correctnessSaving[item.id]"
                        :disabled="!!correctnessSaving[item.id]"
                        title="Сбросить"
                        @click="onIsCorrectChange(item, null)"
                      >
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </template>

                  <template #item.expected_topic="{ item }">
                    <span v-if="item.expected_topic">
                      {{ item.expected_topic }}
                    </span>
                    <v-autocomplete
                      v-else
                      :value="item.expected_topic_id"
                      :items="topicOptionsFor(item)"
                      :loading="!!topicSearchLoading[item.id]"
                      :search-input.sync="topicSearch[item.id]"
                      item-value="id"
                      item-text="title"
                      label="Топик"
                      dense
                      hide-details
                      clearable
                      no-filter
                      no-data-text="Ничего не найдено"
                      class="expected-topic-field"
                      :disabled="!phraseProjectId(item) || !!topicSaving[item.id]"
                      @update:search-input="onTopicSearch(item, $event)"
                      @change="onExpectedTopicChange(item, $event)"
                    />
                  </template>
                </v-data-table>
              </td>
            </template>
          </v-data-table>
        </div>

        <div v-if="paginate.last_page > 1" class="d-flex justify-center mt-4">
          <v-pagination
            v-model="paginate.current_page"
            :length="paginate.last_page"
            :total-visible="10"
            color="secondary"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
            @input="loadReport(false)"
          />
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import ProjectSelector from '~/components/partials/selectors/ProjectSelector.vue'
import TimeInterval from '~/components/pages/stat/elements/TimeInterval.vue'

const SEARCH_DEBOUNCE_MS = 300
const RATING_ALL = 'all'

export default {
  name: 'PrompterControl',

  components: {
    TimeInterval,
    ProjectSelector,
  },

  data() {
    return {
      loading: false,
      reportLoaded: false,
      filters: {
        period: {},
        project: [],
        operator_rating: RATING_ALL,
        sort: 'date:asc',
      },
      sortOptions: [
        { text: 'По дате: сначала ранние', value: 'date:asc' },
        { text: 'По дате: сначала поздние', value: 'date:desc' },
        { text: 'Больше «Не корректно»', value: 'count_incorrect:desc' },
        { text: 'Меньше «Не корректно»', value: 'count_incorrect:asc' },
      ],
      ratingOptions: [
        { text: 'Все', value: RATING_ALL },
        { text: 'Лайк', value: 'like' },
        { text: 'Дизлайк', value: 'dislike' },
      ],
      calls: [],
      summary: null,
      summaryPayload: null,
      expanded: [],
      paginate: {
        total: 0,
        current_page: 1,
        last_page: 1,
        per_page: 15,
      },
      callHeaders: [
        { text: 'Дата звонка', value: 'date', sortable: false },
        { text: 'Проект', value: 'project', sortable: false },
        { text: 'Кол-во фраз', value: 'count_phrases', sortable: false },
        { text: 'Кол-во фраз с найденным ответом', value: 'count_phrases_with_results', sortable: false },
        { text: 'Корректно (РГ)', value: 'correct_ratio', sortable: false },
        { text: 'Лайки (оператор)', value: 'likes_ratio', sortable: false },
        { text: 'Проверено', value: 'is_checked', sortable: false, align: 'center' },
        { text: 'Звонок', value: 'call_link', sortable: false },
      ],
      phraseHeaders: [
        { text: 'Дата', value: 'date', sortable: false },
        { text: 'Фраза абонента', value: 'customer_phrase', sortable: false },
        { text: 'Тема', value: 'topic', sortable: false },
        { text: 'Описание', value: 'topic_description', sortable: false },
        { text: 'Подсказка суфлёра', value: 'operator_phrase', sortable: false },
        { text: 'Оценка оператора', value: 'operator_rating', sortable: false },
        { text: 'Корректность', value: 'is_correct', sortable: false, width: '200px' },
        { text: 'Какой топик должен быть', value: 'expected_topic', sortable: false, width: '260px' },
      ],
      topicSearch: {},
      topicOptions: {},
      topicSearchLoading: {},
      topicSaving: {},
      topicSearchTimers: {},
      correctnessSaving: {},
    }
  },

  computed: {
    projectId() {
      return this.filters.project && this.filters.project.length
        ? this.filters.project[0]
        : null
    },

    canSearch() {
      return !!this.projectId
        && this.filters.period
        && this.filters.period.start
        && this.filters.period.end
    },
  },

  methods: {
    ...mapActions({
      fetchReport: 'prompter_search_results/fetchReport',
      fetchSummary: 'prompter_search_results/fetchSummary',
      searchTopics: 'prompter_search_results/searchTopics',
      updateExpectedTopic: 'prompter_search_results/updateExpectedTopic',
      updateIsCorrect: 'prompter_search_results/updateIsCorrect',
      setError: 'shared/setError',
      setSuccess: 'shared/setSuccess',
    }),

    formatDescription(value) {
      if (!value) return '—'
      return String(value).replace(/\s+/g, ' ').trim()
    },

    formatPercent(value) {
      if (value == null) return '—'
      return `${Math.round(value)} %`
    },

    isLowRatio(value, total) {
      return total > 0 && value / total <= 0.5
    },

    phraseProjectId(phrase) {
      return phrase.project_id || this.projectId
    },

    onCallRowClick(item, slotProps, event) {
      const target = event && event.target
      if (
        target
        && target.closest
        && target.closest('.v-data-table__expand-icon')
      ) {
        return
      }

      if (slotProps && typeof slotProps.expand === 'function') {
        slotProps.expand(!slotProps.isExpanded)
      }
    },

    topicOptionsFor(item) {
      const options = [...(this.topicOptions[item.id] || [])]
      if (
        item.expected_topic_id
        && item.expected_topic
        && !options.some((opt) => Number(opt.id) === Number(item.expected_topic_id))
      ) {
        options.unshift({
          id: item.expected_topic_id,
          title: item.expected_topic,
        })
      }
      return options
    },

    normalizeCalls(calls) {
      return (calls || []).map((call, index) => ({
        ...call,
        items: (call.items || []).map((phrase) => ({
          ...phrase,
          project_id: call.project_id,
        })),
        _rowKey: call.call_link || `call-${call.date}-${call.project_id}-${index}`,
      }))
    },

    buildPayload() {
      const [sortBy, sortDirection] = this.filters.sort.split(':')

      return {
        period: [
          moment(this.filters.period.start).format('YYYY-MM-DD HH:mm:ss'),
          moment(this.filters.period.end).format('YYYY-MM-DD HH:mm:ss'),
        ],
        project_id: this.projectId,
        operator_rating: this.filters.operator_rating === RATING_ALL
          ? null
          : this.filters.operator_rating,
        sort_by: sortBy,
        sort_direction: sortDirection,
        limit: this.paginate.per_page,
        page: this.paginate.current_page,
      }
    },

    async loadReport(resetPage = false) {
      if (!this.$refs.form.validate() || !this.canSearch) return

      if (resetPage) {
        this.paginate.current_page = 1
      }

      this.loading = true
      const payload = this.buildPayload()
      if (resetPage) {
        this.summaryPayload = payload
        this.loadSummary()
      }

      try {
        const response = await this.fetchReport(payload)
        this.calls = this.normalizeCalls(response.calls)
        this.expanded = []
        this.paginate = {
          total: (response.paginate && response.paginate.total) || 0,
          current_page: (response.paginate && response.paginate.current_page) || 1,
          last_page: (response.paginate && response.paginate.last_page) || 1,
          per_page: (response.paginate && response.paginate.per_page) || this.paginate.per_page,
        }
        this.reportLoaded = true
        this.resetTopicSearchState()
      } catch (error) {
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.loading = false
      }
    },

    async loadSummary() {
      const payload = this.summaryPayload
      if (!payload) return

      try {
        const summary = await this.fetchSummary(payload)
        if (payload === this.summaryPayload) {
          this.summary = summary
        }
      } catch (error) {
        const errorPayload = (error.response && error.response.data) || { message: error }
        this.setError(errorPayload)
      }
    },

    recalcCallCounters(phrase) {
      const call = this.calls.find((row) => row.items.includes(phrase))
      if (!call) return

      call.count_checked = call.items.filter((row) => row.is_correct !== null).length
      call.count_correct = call.items.filter((row) => row.is_correct === true).length
      call.is_checked = call.items.length > 0 && call.count_checked === call.items.length
    },

    resetTopicSearchState() {
      Object.keys(this.topicSearchTimers).forEach((key) => {
        clearTimeout(this.topicSearchTimers[key])
      })
      this.topicSearch = {}
      this.topicOptions = {}
      this.topicSearchLoading = {}
      this.topicSaving = {}
      this.topicSearchTimers = {}
      this.correctnessSaving = {}
    },

    onTopicSearch(item, search) {
      const itemId = item.id
      this.$set(this.topicSearch, itemId, search)

      if (this.topicSearchTimers[itemId]) {
        clearTimeout(this.topicSearchTimers[itemId])
      }

      const projectId = this.phraseProjectId(item)
      if (!projectId || !search || !String(search).trim()) {
        this.$set(this.topicOptions, itemId, [])
        return
      }

      this.topicSearchTimers[itemId] = setTimeout(() => {
        this.loadTopics(item, String(search).trim())
      }, SEARCH_DEBOUNCE_MS)
    },

    async loadTopics(item, search) {
      const itemId = item.id
      const projectId = this.phraseProjectId(item)

      this.$set(this.topicSearchLoading, itemId, true)
      try {
        const result = await this.searchTopics({
          projectId,
          search,
        })
        this.$set(this.topicOptions, itemId, result.data || [])
      } catch (error) {
        this.$set(this.topicOptions, itemId, [])
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.$set(this.topicSearchLoading, itemId, false)
      }
    },

    async onExpectedTopicChange(item, expectedTopicId) {
      const nextId = expectedTopicId == null || expectedTopicId === ''
        ? null
        : Number(expectedTopicId)

      const currentId = item.expected_topic_id == null || item.expected_topic_id === ''
        ? null
        : Number(item.expected_topic_id)

      if (nextId === currentId) {
        return
      }

      this.$set(this.topicSaving, item.id, true)
      try {
        const response = await this.updateExpectedTopic({
          id: item.id,
          expectedTopicId: nextId,
        })

        item.expected_topic_id = response.expected_topic_id ?? null
        item.expected_topic = response.expected_topic ?? null

        if (item.expected_topic_id && item.expected_topic) {
          const options = this.topicOptions[item.id] || []
          if (!options.some((opt) => Number(opt.id) === Number(item.expected_topic_id))) {
            this.$set(this.topicOptions, item.id, [
              { id: item.expected_topic_id, title: item.expected_topic },
              ...options,
            ])
          }
        }

        this.setSuccess({ message: 'Сохранено' })
      } catch (error) {
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.$set(this.topicSaving, item.id, false)
      }
    },

    async onIsCorrectChange(item, isCorrect) {
      this.$set(this.correctnessSaving, item.id, true)
      try {
        const response = await this.updateIsCorrect({
          id: item.id,
          isCorrect,
        })

        this.$set(
          item,
          'is_correct',
          response.is_correct !== undefined ? response.is_correct : isCorrect,
        )
        this.recalcCallCounters(item)
        this.loadSummary()
        this.setSuccess({ message: 'Сохранено' })
      } catch (error) {
        const payload = (error.response && error.response.data) || { message: error }
        this.setError(payload)
      } finally {
        this.$set(this.correctnessSaving, item.id, false)
      }
    },
  },

  beforeDestroy() {
    Object.keys(this.topicSearchTimers).forEach((key) => {
      clearTimeout(this.topicSearchTimers[key])
    })
  },
}
</script>

<style scoped>
.table-wrap {
  overflow-x: auto;
}

.call-table >>> .v-data-table__wrapper > table > tbody > tr {
  cursor: pointer;
}

.call-table >>> .v-data-table .v-data-table tbody > tr {
  cursor: default;
}

.description-cell {
  display: inline-block;
  max-width: 280px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rating-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.ratio-cell {
  font-family: monospace;
  white-space: nowrap;
}

.summary-tile + .summary-tile {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.summary-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.summary-value {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}

.summary-number {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}

.expected-topic-field {
  min-width: 220px;
}

.correctness-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.correctness-value {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}
</style>
